import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type StateUpdatedEvent, type GridApi, type GridReadyEvent, type RowClassParams, type PostSortRowsParams } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Input, Button, FormControlLabel, FormControl, FormGroup, Radio, RadioGroup, Checkbox, MenuItem, Select, Tooltip } from "@mui/material";
import Barcode from "react-barcode";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";
import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

import "./inout_work.scoped.scss";

import StockInfoPopup from "@user/views/inoutbound/popup_stock_info";
import SkuReqPopup from "@user/views/inoutbound/popup_sku_req";
import TrSnChangePopup from "@user/views/inoutbound/popup_tr_sn_change";
import PrintTrtagPopup from "@user/views/popup/popup_print_trtag";
import InoutWorkPopup from "@user/views/sample/popup_inout_work";

import PrintLabel, { type PrintLabelHandle } from "@user/views/print/print_label";
import PrintTicket, { type PrintTicketHandle } from "@user/views/print/print_ticket";

interface State {
  gridApi: GridApi | null,
  workOrderType: string,
  ticketType: string,
  priorityType: string,
  workStationId: string,
  workReqInfos: any[],
  isShowStockInfo: boolean,
  isShowSkuReq: boolean,
  trSnInfo: object,
  isShowTrSnChange: boolean,
  printTrtagInfos: any[],
  isShowPrintTrtag: boolean,
  isShowInoutWork: boolean
}

const defState: State = {
  gridApi: null,
  workOrderType: "M",
  ticketType: "2",
  priorityType: "N",
  workStationId: "",
  workReqInfos: [],
  isShowStockInfo: false,
  isShowSkuReq: false,
  trSnInfo: {},
  isShowTrSnChange: false,
  printTrtagInfos: [],
  isShowPrintTrtag: false,
  isShowInoutWork: false
};

interface SearchParams {
  inoutType: string,
  inoutDetailTypes: string,
  workReqStatuses: string,
  wh: string,
  trackingIdLike: string,
  woLike: string,
  pnLike: string,
  hlLike: string,
  requesterLike: string,
  visitNoLike: string,
  toSubinvLike: string,
  export: string,
  page: number,
  pageSize: number
}

const defSearchParams: SearchParams = {
  inoutType: "",
  inoutDetailTypes: "",
  workReqStatuses: "",
  wh: "",
  trackingIdLike: "",
  woLike: "",
  pnLike: "",
  hlLike: "",
  requesterLike: "",
  visitNoLike: "",
  toSubinvLike: "",
  export: "",
  page: 1,
  pageSize: kutil.DEFAULT_PAGE_SIZE
};

interface Result {
  searchParams: SearchParams,
  pageInfo: PageInfo,
  infos: any[]
}

const defResult = {
  searchParams: kutil.deepCopy(defSearchParams),
  pageInfo: {
    totalCount: 0,
    totalPage: 0,
    page: defSearchParams.page,
    pageSize: defSearchParams.pageSize
  },
  infos: []
};

const validationMsg4orderWork = [
  {id: "workReqErpNo", type: "InvalidStatus", msg: "작업대기 상태만 작업지시할 수 있습니다."},
  {id: "workInfos", type: "NotFound", msg: "선택SKU가 있는 작업만 작업지시할 수 있습니다."},
  {id: "skuNo", type: "InvalidStatus", msg: (_p: any, e: any) => `Tracking ID(${e.value}) 현재 작업대에서 호출할 수 없는 SKU입니다.`},
  {id: "eaiId", type: "InvalidStatus", msg: (_p: any, e: any) => `${e.desc}`}
];

const validationMsg4cancelWork = [
  {id: "reqInfos", type: "NotBlank", msg: "작업을 선택해 주세요."},
  {id: "workReqErpNo", type: "InvalidStatus", msg: "작업을 시작하지 않은 작업만 작업삭제할 수 있습니다."},
  {id: "eaiId", type: "InvalidStatus", msg: (_p: any, e: any) => `${e.desc}`}
];

const validationMsg4holdWork = [
  {id: "reqInfos", type: "NotBlank", msg: "작업을 선택해 주세요."},
  {id: "workReqErpNo", type: "InvalidStatus", msg: "작업대기 상태만 작업보류할 수 있습니다."}
];

function InoutWork(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const isActiveTab = useTabsStore((state) => state.currTabId == props.id);
  const location = useTabsStore((state) => state.getLocation(props.id));
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(location.path), [location.path]);

  const printLabelRef = useRef<PrintLabelHandle>(null);
  const printTicketRef = useRef<PrintTicketHandle>(null);

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "", field: "", width: 50,
      cellStyle: (p: any) => {
        if (p.data.workReqStatus == "W") {
          return { backgroundColor: "#57bbeb" };
        } else if (Number(p.data.completeQty) > 0 && Number(p.data.remainQty) > 0) {
          return { backgroundColor: "#ffa52c" };
        } else if (p.data.workReqStatus == "I") {
          return { backgroundColor: "#28794e" };
        }
      }
    },
    { headerName: "작업구분", field: "inoutType", width: 80, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("inoutType") },
    { headerName: "상세구분", field: "inoutDetailType", width: 80, cellStyle: { textAlign: "center"}, valueFormatter: valueFormatter.commonCode("inoutDetailType") },
    { headerName: "작업상태", field: "workReqStatus", width: 80, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("workReqStatus") },
    { headerName: "선택SKU", field: "choiceSkuType", width: 80, cellStyle: {textAlign: "center"},
      valueGetter: (p: any) => {
        if (p.node?.rowPinned) return "";
        if (p.data.choiceSkuType != null && p.data.choiceSkuCallQty != null && p.data.choiceSkuStockQty != null) {
          return (p.data.choiceSkuType + "(" + p.data.choiceSkuCallQty + "/" + p.data.choiceSkuStockQty +")");
        }
      },
    },
    { headerName: "Tracking ID", field: "trackingId", width: 120, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "출력가능", field: "", width: 100, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]},
      valueGetter: (p: any) => {
        if (p.node?.rowPinned) return "";
        const printText = [];
        if (p.data.ticketFileName) printText.push("티켓");
        if (p.data.trFileName) printText.push("TR");
        return printText.length ? printText.join("/") : "불가";
      },
    },
    { headerName: "Creat Date", field: "createDate", width: 180, cellStyle: { textAlign: "center" }, valueFormatter: valueFormatter.date("yyyy-MM-dd HH:mm:ss"), filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "창고", field: "wh", width: 80, cellStyle: { textAlign: "center"}, valueFormatter: valueFormatter.commonCode("wh") },
    { headerName: "P/N", headerClass: "hd-ta-left", field: "pn", width: 160, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "TR No", headerClass: "hd-ta-left", field: "trNo", width: 150, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "S/N", field: "sn", headerClass: "hd-ta-left", width: 150, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "TR/SN 변경", field: "", width: 90, cellStyle: kutil.aggridButtonCellStyle,
      cellRenderer: (p: any) => {
        if (p.node?.rowPinned) return null;
        if (!auth.write) return null;
        return (<button className="icon-button grid-write" onClick={() => updateTrSn(p.data)}>변경</button>);
      }
    },
    { headerName: "요청자", field: "requester", width: 100, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "KWD", headerClass: "hd-ta-left", field: "kwd", width: 180, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Request Qty", headerClass: "hd-ta-right", field: "reqQty", width: 130, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Remain Qty", headerClass: "hd-ta-right", field: "remainQty", width: 130, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "UOM", field: "uom", width: 80, cellStyle: {textAlign: "center"} , filter: true, filterParams: {buttons: ["reset"]}},
    { headerName: "CTG", headerClass: "hd-ta-left",  field: "ctg", width: 120, cellStyle: {textAlign: "left"} , filter: true, filterParams: {buttons: ["reset"]}},
    { headerName: "긴급여부", field: "urgentStatus", width: 80, cellStyle: {textAlign: "center"},
      valueGetter: (p: any) => {
        if (p.node?.rowPinned) return "";
        if (p.data.urgentStatus === "4") return "";
        return commonCode.getCodeValue("urgentStatus", p.data.urgentStatus);
      }
    },
    { headerName: "To Subinv", headerClass: "hd-ta-left", field: "toSubinv", width: 120, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "To Locator", headerClass: "hd-ta-left", field: "toLocator", width: 120, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Work Shop", headerClass: "hd-ta-left", field: "workShop", width: 120, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "W/O", headerClass: "hd-ta-left", field: "wo", width: 150, cellStyle: { textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Visit No", headerClass: "hd-ta-left", field: "visitNo", width: 120, cellStyle: { textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "기번(HL)", field: "hl", width: 120, cellStyle: { textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Complete Qty", headerClass: "hd-ta-right", field: "completeQty", width: 130, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "REMARK", headerClass: "hd-ta-left", field: "remark", width: 250, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "작업대", field: "workStationId", width: 80, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("workStationId") }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridOptions = useMemo(() => ({
    getRowId: (p: any) => String(p.data.workReqErpNo),
    getRowStyle: (p: RowClassParams) => {
      if (p.data.ticketFilePrintDate) {
        return {"backgroundColor": "#FFE2E2"};
      }
    },
    rowSelection: {
      mode: "multiRow" as const,
    },
    selectionColumnDef: {
      resizable: true
    },
    postSortRows: (params: PostSortRowsParams) => {
      const nodes = params.nodes;
      const top = nodes.filter(n => n.data?.urgentStatus === "1" || n.data?.urgentStatus === "2" || n.data?.urgentStatus === "3");
      const rest = nodes.filter(n => n.data?.urgentStatus !== "1" && n.data?.urgentStatus !== "2" && n.data?.urgentStatus !== "3");

      top.sort((a, b) => {
        const statusDiff = Number(a.data.urgentStatus) - Number(b.data.urgentStatus);  // 1 → 2 → 3
        if (statusDiff !== 0) return statusDiff;
        return (b.data.createDate ?? "").localeCompare(a.data.createDate ?? "");       // 최신 순
      });
      nodes.splice(0, nodes.length, ...top, ...rest);
    },
  }), []);
  const gridCallbacks = useStableHandlers({
    onGridReady: (e: GridReadyEvent) => {
      setState(prev => ({ ...prev, gridApi: e.api }));
    },
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, location.path);
    },
  });

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);

  // 바코드 스캔 기능, 단축키 적용
  const onBarcodeScanRef = useRef(onBarcodeScan);
  const onKeyboardShortcutRef = useRef(onKeyboardShortcut);
  useEffect(() => {
    onBarcodeScanRef.current = onBarcodeScan;
    onKeyboardShortcutRef.current = onKeyboardShortcut;
  });

  useEffect(() => {
    if (!isActiveTab || state.isShowStockInfo || state.isShowSkuReq || state.isShowTrSnChange || state.isShowPrintTrtag || state.isShowInoutWork) {
      return;
    }

    // 바코드 스캔 핸들러
    const barcodeHandler = (code: string) => onBarcodeScanRef.current(code);
    kutil.startBarcodeScan(barcodeHandler);

    // 키보드 단축키 핸들러
    const shortcuthandler = (code: string) => onKeyboardShortcutRef.current(code);
    kutil.startKeyboardShortcut([
      {id: "F1", altKey: false, ctrlKey: false, shiftKey: false, key: "F1"},
      {id: "F2", altKey: false, ctrlKey: false, shiftKey: false, key: "F2"},
      {id: "F3", altKey: false, ctrlKey: false, shiftKey: false, key: "F3"},
    ], shortcuthandler);

    return () => {
      kutil.stopBarcodeScan(barcodeHandler)
      kutil.stopKeyboardShortcut(shortcuthandler)
    };
  }, [isActiveTab, state.isShowStockInfo, state.isShowSkuReq, state.isShowTrSnChange, state.isShowPrintTrtag, state.isShowInoutWork]);
  // 끝: 바코드 스캔 기능, 단축키 적용

  // 작업대ID 기본값 지정
  useEffect(() => {
    const workStationId = useSettingsStore.getState().getDefWorkStationId(location.path) || (commonCode.workStationId as any[])?.[0]?.code || "";
    setState(prev => ({ ...prev, workStationId}));
  }, [location.path]);

  // 변경 작업대ID 저장
  useEffect(() => {
    if (state.workStationId) {
      useSettingsStore.getState().setLocalSetting("workStationId", location.path, state.workStationId);
    }
  }, [state.workStationId]);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        const options: any = useSettingsStore.getState().getSetting("options." + location.path);
        if (options) {
          setState(prev => ({ ...prev,
            workOrderType: options?.workOrderType ?? defState.workOrderType,
            ticketType: options?.ticketType ?? defState.ticketType
          }));
        }
        isInit.current = await initData();
      }
      if (isInit.current) {
        state.gridApi?.deselectAll();
        state.gridApi?.ensureIndexVisible(0, "top");
        await initParams();
        await searchInfos();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const searchParams = kutil.deepCopy(defSearchParams);
    kutil.copyObj(Object.fromEntries(new URLSearchParams(location.search).entries()), searchParams);

    if (!("inoutDetailTypes" in Object.fromEntries(new URLSearchParams(location.search)))) {
      searchParams.inoutDetailTypes = (commonCode.inoutDetailType as any[])?.map(c => c.code).join(",");
    }
    if (!("workReqStatuses" in Object.fromEntries(new URLSearchParams(location.search)))) {
      searchParams.workReqStatuses = (commonCode.workReqStatus as any[])?.filter(c => c.code === "W").map(c => c.code).join(",");
    }
    updateSearchParams(searchParams);
  }

  async function searchInfos() {
    const searchParams = searchParamsRef.current;

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams);
      const data = await kutil.sendGet("/api/inoutbound/inout_work.data?mode=search", reqSearchParams, {});
      setResult({
        searchParams: reqSearchParams,
        pageInfo: data.pageInfo,
        infos: data.infos,
      });
    } catch(e) {
      console.log(e);
    }
  }

  async function doSearch(_e: any, reqParams: any|null = null) {
    const searchParams = updateSearchParams({ ...reqParams, page: 1 });
    router.navigateByPath("/inoutbound/inout_work", searchParams);
  }

  const goPage = useCallback((page: number) => {
    const searchParams = updateSearchParams({ ...result.searchParams, page }, true);
    router.navigateByPath("/inoutbound/inout_work", searchParams);
  }, [result, router, updateSearchParams]);

  async function resetParams() {
    router.navigateByPath("/inoutbound/inout_work", {});
  }

  async function changeInoutDetailType(e: any) {
    doSearch(e, {inoutDetailTypes: kutil.joinCheckBoxArr(searchParams.inoutDetailTypes, e.target.value, e.target.checked)});
  }

  async function changeWorkReqStatus(e: any) {
    doSearch(e, {workReqStatuses: kutil.joinCheckBoxArr(searchParams.workReqStatuses, e.target.value, e.target.checked)});
  }

  async function orderWork() {
    const selectedInfos = state.gridApi!.getSelectedRows();
    if (selectedInfos.length === 0) {
      await kutil.alert("작업지시 작업을 선택해 주세요.");
      return;
    }

    const infos = (selectedInfos as any[]).filter(item => item.workReqStatus != "W");
    if (infos.length > 0) {
      await kutil.alert("작업대기 상태만 작업지시할 수 있습니다.");
      return;
    }

    const multiInfos: any[] = [];

    const mismatchProc = async (info: any, stockQty: number | string, message: string) => {
      info.reqQty = info.remainQty;
      info.stockQty = stockQty;
      await kutil.alert(message);
      await kutil.sendPost("/api/inoutbound/inout_work.proc?mode=addMismatchHist", info, {});
    };

    for (const info of selectedInfos) {
      try {
        const data = await kutil.sendGet("/api/inoutbound/inout_work.data?mode=searchSkuStock", { mngType: info.mngType, wh: info.wh, pn: info.pn, trNo: info.trNo, sn: info.sn, workStationId: state.workStationId }, {});
        info.workStationId = state.workStationId;
        info.priorityType = state.priorityType;

        if (data.infos.length === 0) {
          if (info.inoutType === "O") {
            await mismatchProc(info, "0", `Tracking ID(${info.trackingId}) SKU가 없습니다.`);
          } else {
            await kutil.alert(`Tracking ID(${info.trackingId}) SKU가 없습니다.`);
          }
          continue;
        }

        if (info.inoutType === "O") {
          const wo = (info.wo ?? "").trim();
          const toSubinv = (info.toSubinv ?? "").trim();
          const isCWo = wo.toUpperCase().startsWith("C");
          // To Subinv가 없고, C로 시작하는 W/O(예외)도 아니면 작업지시 차단
          if (!toSubinv && !isCWo) {
            await kutil.alert(`Tracking ID(${info.trackingId}) To Subinv 정보가 없습니다.`);
            continue;
          }
        }

        // 출고: orderPossYn == Y 이고 작업대가 같아야 가능 / 그 외: outPossYn == Y
        const isPoss = (row: any) => info.inoutType === "O" ? row.orderPossYn == "Y" && (!row.workStationId || row.workStationId == info.workStationId) : row.outPossYn == "Y";
        if (!data.infos.some(isPoss)) {
          await kutil.alert(`Tracking ID(${info.trackingId}) 현재 작업대에서 호출할 수 없는 SKU입니다.`);
          continue;
        }

        if (data.infos.length === 1) {
          const skuStock = data.infos[0];
          if (info.inoutType === "O" && Number(info.remainQty) > Number(skuStock.stockQty)) {
            await mismatchProc(info, skuStock.stockQty, `Tracking ID(${info.trackingId}) 재고수량을 초과하였습니다.`);
            continue;
          }
          info.choiceSkuType = "S";
          info.choiceSkuCallQty = "1";
          info.choiceSkuStockQty = "1";
          info.workInfos = data.infos;
          info.workInfos[0].jobQty = info.remainQty;
          await kutil.sendPost("/api/inoutbound/inout_work.proc?mode=orderWork", info, {
            validationMsg: validationMsg4orderWork
          });
          continue;
        }

        const stockQty = data.infos.filter(isPoss).reduce((sum: number, row: any) => sum + (Number(row.stockQty) || 0), 0);
        if (info.inoutType === "O" && (stockQty === 0 || Number(info.remainQty) > stockQty)) {
          await mismatchProc(info, stockQty, `Tracking ID(${info.trackingId}) 재고수량을 초과하였습니다.`);
          continue;
        }

        info.skuStockInfos = data.infos.map((row: any) => ({ ...row, possYn: isPoss(row) ? "Y" : "N" }));
        multiInfos.push(info);
      } catch(e) {
        console.log(e);
      }
    }

    if (multiInfos.length > 0) {
      openPopupStockInfo(multiInfos);
    } else {
      setState(prev => ({ ...prev, priorityType: "N" }));
      router.reload();
    }
  }

  async function reqSku() {
    openPopupSkuReq();
  }

  async function cancelWork() {
    const selectedInfos = state.gridApi!.getSelectedRows();
    if (selectedInfos.length == 0) {
      kutil.alert("작업삭제 작업을 선택해 주세요.");
      return;
    }

    const infos = (selectedInfos as any[]).filter(item => item.workReqStatus != "W" || item.reqQty != item.remainQty);
    if (infos.length > 0) {
      await kutil.alert("작업을 시작하지 않은 작업만 작업삭제할 수 있습니다.");
      return;
    }

    if (await kutil.confirm("해당 작업지시정보를 ERP에 삭제 요청하시겠습니까?") == false) {
      return false;
    }

    let success = 0; const fails: string[] = [];
    for (const info of selectedInfos) {
      try {
        const data = await kutil.sendPost("/api/inoutbound/inout_work.proc?mode=cancelWork", [info], {
          validationMsg: validationMsg4cancelWork
        });
        success++;
      } catch (e) {
        fails.push(info.workReqErpNo);
      }
    }

    await kutil.alert(`작업삭제하였습니다.\r\n성공 ${success}건${fails.length ? `, 실패 ${fails.length}건` : ""}`);
    router.reload();
  }

  async function holdWork() {
    const selectedInfos = state.gridApi!.getSelectedRows();
    if (selectedInfos.length == 0) {
      kutil.alert("작업보류 작업을 선택해 주세요.");
      return;
    }

    const infos = (selectedInfos as any[]).filter(item => item.workReqStatus != "W");
    if (infos.length > 0) {
      await kutil.alert("작업대기 상태만 작업보류할 수 있습니다.");
      return;
    }

    if (await kutil.confirm("작업보류하시겠습니까?") == false) {
      return false;
    }

    try {
      const data = await kutil.sendPost("/api/inoutbound/inout_work.proc?mode=holdWork", selectedInfos, {
        validationMsg: validationMsg4holdWork
      });
      await kutil.alert("작업보류하였습니다.");
      router.reload();
    } catch(e) {
      console.log(e);
    }
  }

  async function printTrtag() {
    openPopupPrintTrtag();
  }

  async function printLabel() {
    const selectedInfos = state.gridApi!.getSelectedRows();
    if (selectedInfos.length === 0) {
      await kutil.alert("라벨 출력 작업을 선택해 주세요.");
      return;
    }

    await printLabelRef.current?.print(selectedInfos);
  }

  async function printTicket() {
    const api = state.gridApi!;
    const selectedNodes = api.getSelectedNodes();
    const selectedInfos = selectedNodes.map(n => n.data);
    if (selectedInfos.length === 0) {
      await kutil.alert("티켓 출력 작업을 선택해 주세요.");
      return;
    }

    if (selectedInfos.some(item => !item.ticketFileName)) {
      await kutil.alert("티켓 파일이 있는 작업만 출력할 수 있습니다.");
      return;
    }

    try {
      await kutil.sendPost("/api/inoutbound/inout_work.proc?mode=updateTicketFilePrintDate", selectedInfos, {});
      await printTicketRef.current?.print({
        infos: selectedInfos,
        ticketType: state.ticketType,
      });

      const printDate = kutil.date2string(kutil.getCurrLocalDate(), "yyyyMMddHHmmss");
      selectedNodes.forEach(node => node.setData({ ...node.data, ticketFilePrintDate: printDate }));
      api.redrawRows({ rowNodes: selectedNodes });
    } catch (e) {
      console.log(e);
    }
  }

  async function undoTicket() {
    const selectedInfos = state.gridApi!.getSelectedRows();
    if (selectedInfos.length === 0) {
      await kutil.alert("티켓출력이력 초기화 작업을 선택해 주세요.");
      return;
    }

    const infos = (selectedInfos as any[]).filter(item => !item.ticketFilePrintDate);
    if (infos.length > 0) {
      await kutil.alert("티켓출력이력이 있는 작업만 초기화할 수 있습니다.");
      return;
    }

    for (const info of selectedInfos) {
      info.ticketFileInitYn = "Y";
    }

    if (await kutil.confirm("티켓출력이력을 초기화하시겠습니까?") == false) {
      return false;
    }

    try {
      const data = await kutil.sendPost("/api/inoutbound/inout_work.proc?mode=updateTicketFilePrintDate", selectedInfos, {});
      router.reload();
    } catch(e) {
      console.log(e);
    }
  }

  async function downloadExcel() {
    const searchParams = kutil.copyObj(result.searchParams, {} as SearchParams);
    searchParams.export = "excel";
    searchParams.page = 1;
    searchParams.pageSize = 10000;
    try {
      await kutil.downloadFile("/api/inoutbound/inout_work.download?mode=search", searchParams, {
        "fileName": "입출고작업_" + kutil.date2string(kutil.getCurrLocalDate(), "yyyyMMddHHmmss") + ".xlsx"
      });
    } catch(e) {
      console.log(e);
    }
  }

  async function updateTrSn(info: any) {
    openPopupTrSnChange(info);
  }

  async function openPopupStockInfo(infos: any[]) {
    setState(prev => ({ ...prev, isShowStockInfo: true, workReqInfos: infos}));
  }

  async function closePopupStockInfo(data: any) {
    setState(prev => ({ ...prev, isShowStockInfo: false }));

    if (data?.isSuccess) {
      setState(prev => ({ ...prev, priorityType: "N" }));
      router.reload();
    }
  }

  async function openPopupSkuReq() {
    setState(prev => ({ ...prev, isShowSkuReq: true }));
  }

  async function closePopupSkuReq(_data: any) {
    setState(prev => ({ ...prev, isShowSkuReq: false }));
  }

  async function openPopupTrSnChange(info: any) {
    setState(prev => ({ ...prev, isShowTrSnChange: true, trSnInfo: info}));
  }

  async function closePopupTrSnChange(data: any) {
    setState(prev => ({ ...prev, isShowTrSnChange: false }));

    if (data?.isSuccess) {
      router.reload();
    }
  }

  async function openPopupPrintTrtag() {
    const selectedInfos = state.gridApi!.getSelectedRows();
    if (selectedInfos.length === 0) {
      await kutil.alert("TR Tag 출력 작업을 선택해 주세요.");
      return;
    }

    const infos = (selectedInfos as any[]).filter(item => !item.trFileName);
    if (infos.length > 0) {
      await kutil.alert("TR Tag 파일이 있는 작업만 출력할 수 있습니다.");
      return;
    }

    setState(prev => ({ ...prev, isShowPrintTrtag: true, printTrtagInfos: selectedInfos}));
  }

  async function closePopupPrintTrtag(_data: any) {
    setState(prev => ({ ...prev, isShowPrintTrtag: false }));
  }

  async function openPopupInoutWork() {
    setState(prev => ({ ...prev, isShowInoutWork: true}));
  }

  async function closePopupInoutWork(data: any) {
    setState(prev => ({ ...prev, isShowInoutWork: false }));

    if (data?.isSuccess) {
      router.reload();
    }
  }

  function selectTrackingId(trackingId: string) {
    const api = state.gridApi;
    if (!api) return;

    let found = false;
    api.forEachNode((node) => {
      if (node.data?.trackingId === trackingId) {
        node.setSelected(true);
        api.ensureNodeVisible(node, "middle");
        found = true;
      }
    });

    if (!found) {
      kutil.alert("일치하는 Tracking ID가 없습니다.\r\n" + trackingId);
    }
  }

  async function onBarcodeScan(code: string) {
    const [prefix, ...rest] = code.split(":");
    const value = rest.join(":").trim();

    if (rest.length > 0) {
      switch (prefix) {
        case "WS": {
          if (!value) return;
          const exists = (commonCode.workStationId as any[])?.some(c => c.code === value);
          if (!exists) {
            kutil.alert("일치하는 작업대가 없습니다.\r\n" + code);
            return;
          }
          setState(prev => ({ ...prev, workStationId: value }));
          return;
        }
        default:
          return;
      }
    }

    if (code == "WORK_ORDER") {
      orderWork();
    } else {
      selectTrackingId(code);
      if (state.workOrderType == "S") {
        orderWork();
      }
    }
  }

  async function onKeyboardShortcut(shortcut: string) {
    if (shortcut == "F1") {
      doSearch(null, null);
    } else if (shortcut == "F2") {
      orderWork();
    } else if (shortcut == "F3") {
      reqSku();
    }
  }

  return (
    <>
      {/* contents */}
      <div className="contents">
        {/* contents 상단 */}
        <div className="contents-top">
          {/* 타이틀 영역 */}
          <div className="title-area">
            {/* 타이틀 */}
            <p>입출고 작업</p>
          </div>
          {/*// 타이틀 영역 */}
          {/* 버튼 영역 */}
          <div className="top-btn-area">
            <Button variant="contained" onClick={openPopupInoutWork}>ERP 작업지시 등록(임시)</Button>
            <Tooltip title="단축키 [F1]">
              <Button variant="outlined" onClick={doSearch}>조회</Button>
            </Tooltip>
            { auth.download &&
            <Button variant="outlined" onClick={downloadExcel}>엑셀다운</Button>
            }
            { auth.func1 &&
            <Button variant="outlined" onClick={cancelWork}>작업삭제</Button>
            }
            { auth.download &&
            <Button variant="outlined" onClick={printLabel}>라벨 출력</Button>
            }
            { auth.download &&
            <Button variant="outlined" onClick={printTrtag}>TR Tag 출력</Button>
            }
            { auth.download &&
            <Button variant="outlined" onClick={printTicket}>티켓 출력</Button>
            }
            { auth.write &&
            <Button variant="outlined" onClick={undoTicket}>Undo 티켓</Button>
            }
            { auth.write &&
              <Button variant="outlined" onClick={holdWork}>작업보류</Button>
            }
            { auth.write &&
            <Tooltip title="단축키 [F3]">
              <Button variant="outlined" onClick={reqSku}>범위지정</Button>
            </Tooltip>
            }
            { auth.write &&
              <Tooltip title="단축키 [F2]">
              <Button variant="contained" onClick={orderWork}>작업지시</Button>
              </Tooltip>
            }
          </div>
          {/*// 버튼 영역 */}
        </div>
        {/*// contents 상단 */}
        {/* 검색 */}
        <div className="work-srch-area">
          <div>
            <p>작업구분</p>
            <RadioGroup
              value={searchParams.inoutType}
              onChange={(e) => {doSearch(e, {inoutType: e.target.value})}}
            >
              <FormControlLabel value="" control={<Radio size="small" />} label="전체" />
              {(commonCode.inoutType as any[])?.map((code) => (
                <FormControlLabel key={code.code} value={code.code} control={<Radio size="small" />} label={code.value} />
              ))}
            </RadioGroup>
          </div>
          <div className="divider ver"></div>
          <div>
            <p>상세구분</p>
            <FormGroup>
                {(commonCode.inoutDetailType as any[])?.map((code) => (
                  <FormControlLabel key={code.code} value={code.code} control={<Checkbox size="small" checked={searchParams.inoutDetailTypes.split(",").includes(code.code)} />} label={code.value} onChange={changeInoutDetailType} />
                ))}
            </FormGroup>
          </div>
          <div className="divider ver"></div>
          <div>
            <p>작업상태</p>
            <FormGroup>
                {(commonCode.workReqStatus as any[])?.filter(c => c.code === "W" || c.code === "I").map((code) => (
                  <FormControlLabel key={code.code} value={code.code} control={<Checkbox size="small" checked={searchParams.workReqStatuses.split(",").includes(code.code)} />} label={code.value} onChange={changeWorkReqStatus} />
                ))}
            </FormGroup>
          </div>
          <div className="divider ver"></div>
          <div className="srch_item">
            <div>
              <p>창고</p>
              <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                <Select
                  value={searchParams.wh}
                  onChange={(e) => {doSearch(e, {wh: e.target.value});}}
                  displayEmpty
                >
                  <MenuItem value="">전체</MenuItem>
                  {(commonCode.wh as any[])?.filter(c => c.code !== "B").map((code) => (
                    <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div>
              <p>Tracking ID</p>
              <Input
                inputProps={{ maxLength: 100 }}
                value={searchParams.trackingIdLike}
                onChange={(e) => {
                  const value = e.target.value;
                  updateSearchParams({ trackingIdLike: value });
                  if (value.length === 8) {
                    doSearch(e, null);
                  }
                }}
                onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
                placeholder="Tracking ID"
              />
            </div>
            <div>
              <p>P/N</p>
              <Input
                inputProps={{ maxLength: 100 }}
                value={searchParams.pnLike}
                onChange={(e) => { updateSearchParams({ pnLike: e.target.value }) }}
                onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
                placeholder="P/N"
              />
            </div>
            <div>
              <p>W/O</p>
              <Input
                inputProps={{ maxLength: 100 }}
                value={searchParams.woLike}
                onChange={(e) => {updateSearchParams({ woLike: e.target.value })}}
                onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
                placeholder="W/O"
              />
            </div>
            <div>
              <p>기번(HL)</p>
              <Input
                inputProps={{ maxLength: 100 }}
                value={searchParams.hlLike}
                onChange={(e) => {updateSearchParams({ hlLike: e.target.value })}}
                onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
                placeholder="기번(HL)"
              />
            </div>
            <div>
              <p>요청자</p>
              <Input
                inputProps={{ maxLength: 100 }}
                value={searchParams.requesterLike}
                onChange={(e) => { updateSearchParams({ requesterLike: e.target.value }) }}
                onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
                placeholder="요청자"
              />
            </div>
            <div>
              <p>To Subinv</p>
              <Input
                inputProps={{ maxLength: 100 }}
                value={searchParams.toSubinvLike}
                onChange={(e) => { updateSearchParams({ toSubinvLike: e.target.value }) }}
                onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
                placeholder="To Subinv"
              />
            </div>
            <div>
              <p>Visit No</p>
              <Input
                inputProps={{ maxLength: 100 }}
                value={searchParams.visitNoLike}
                onChange={(e) => { updateSearchParams({ visitNoLike: e.target.value }) }}
                onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
                placeholder="Visit No"
              />
            </div>
          </div>
          <span>
            { auth.write &&
            <em>
              <Barcode value="WORK_ORDER" format="CODE128" width={1} height={28} displayValue={false} background="#f7f7f7" margin={0} />
              <div>
                <i></i>
                작업지시
              </div>
            </em>
            }
          </span>
          <p>
            <Tooltip title="초기화">
              <button className="icon-button form-reset" onClick={resetParams}>초기화</button>
            </Tooltip>
          </p>
        </div>
        {/*// 검색 */}
        {/* 그리드 */}
        <AgGridProvider modules={modules}>
          <div className="grid-area">
            <CustomAgGridReact
              columnDefs={gridInfo}
              rowData={result.infos}
              defaultColDef={kutil.defaultColDef}
              initialState={gridState}
              getRowId={gridOptions.getRowId}
              getRowStyle={gridOptions.getRowStyle}
              rowSelection={gridOptions.rowSelection}
              selectionColumnDef={gridOptions.selectionColumnDef}
              postSortRows={gridOptions.postSortRows}
              onGridReady={gridCallbacks.onGridReady}
              onStateUpdated={gridCallbacks.onStateUpdated}
            />
          </div>
        </AgGridProvider>
        {/*// 그리드 */}
        {state.isShowStockInfo && <StockInfoPopup isShow={state.isShowStockInfo} onClose={closePopupStockInfo} id={props.id} data={{infos: state.workReqInfos}} />}
        {state.isShowSkuReq && <SkuReqPopup isShow={state.isShowSkuReq} onClose={closePopupSkuReq} id={props.id} data={{path: "inout_work", workStationId: state.workStationId}} />}
        {state.isShowTrSnChange && <TrSnChangePopup isShow={state.isShowTrSnChange} onClose={closePopupTrSnChange} id={props.id} data={{path: "inout_work", info: state.trSnInfo}} />}
        {state.isShowPrintTrtag && <PrintTrtagPopup isShow={state.isShowPrintTrtag} onClose={closePopupPrintTrtag} id={props.id} data={{infos: state.printTrtagInfos}} />}
        {state.isShowInoutWork && <InoutWorkPopup isShow={state.isShowInoutWork} onClose={closePopupInoutWork} />}
        <PrintLabel ref={printLabelRef} />
        <PrintTicket ref={printTicketRef} />
        {/* 옵션 */}
        <div className="work_option">
          <div>
            <p>바코드 옵션</p>
            <RadioGroup
              row
              value={state.workOrderType}
                onChange={(e) => {
                  setState(prev => ({ ...prev, workOrderType: e.target.value }));
                  useSettingsStore.getState().setSetting("options." + location.path, {
                    workOrderType: e.target.value,
                    ticketType: state.ticketType
                  }, 0);
                }}
            >
              {(commonCode.workOrderType as any[])?.map((code) => (
                <FormControlLabel key={code.code} value={code.code} control={<Radio size="small" />} label={code.value} />
              ))}
            </RadioGroup>
          </div>
          <div className="divider ver"></div>
          <div>
            <p>티켓 옵션</p>
            <RadioGroup
              row
              value={state.ticketType}
                onChange={(e) => {
                  setState(prev => ({ ...prev, ticketType: e.target.value }));
                  useSettingsStore.getState().setSetting("options." + location.path, {
                    workOrderType: state.workOrderType,
                    ticketType: e.target.value,
                  }, 0);
                }}
            >
              {(commonCode.ticketType as any[])?.map((code) => (
                <FormControlLabel key={code.code} value={code.code} control={<Radio size="small" />} label={code.value} />
              ))}
            </RadioGroup>
          </div>
          <div className="divider ver"></div>
          <div>
            <p>우선순위</p>
            <FormControl sx={{ m: 1, minWidth: "100%" }} size="small">
              <Select
                value={state.priorityType}
                onChange={(e) => {setState(prev => ({ ...prev, priorityType: e.target.value }))}}
                displayEmpty
              >
                {(commonCode.priorityType as any[])?.map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className="divider ver"></div>
          <div>
            <p>작업대</p>
            <FormControl sx={{ m: 1, minWidth: "100%" }} size="small">
              <Select
                value={state.workStationId}
                onChange={(e) => {setState(prev => ({ ...prev, workStationId: e.target.value }))}}
                displayEmpty
              >
                {(commonCode.workStationId as any[])?.map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>
        {/*// 옵션 */}
      </div>
      {/*// contents */}
    </>
  );
}

export default React.memo(InoutWork);
